import React, { useCallback, useEffect, useState } from 'react'
import { Badge, Button, Dropdown, Form, Spinner, Tab, Tabs } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { SETTINGS_KEYS, environment } from '../../config/environment'
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination'
import { useSetting } from '../../hooks/useSettings'
import { permissionsService } from '../../services/permissions.service'
import { settingsService } from '../../services/settings.service'
import { PermissionAction, PermissionType } from '../../services/permissions.service'
import { taskService } from '../../services/task.service'
import { DocDotTask } from '../../types'
import { formatDate } from '../../utils/dateFormatter'
import IfPermissions from '../common/IfPermissions'
import PageHeader from '../common/PageHeader'
import { Pagination } from '../common/Pagination'
import './TaskList.scss'

export enum TaskTab {
  Queued = 'queued',
  Started = 'started',
  Completed = 'completed',
  Failed = 'failed',
}

interface TaskListProps {
  className?: string
}

export const TaskList: React.FC<TaskListProps> = ({ className }) => {
  const TASK_PAGE_SIZE_STORAGE_KEY = 'taskListPageSize'
  const getTaskPageSizeStorageKey = () => {
    const userId = permissionsService.getCurrentUser()?.id
    const orgId = settingsService.getCurrentOrganizationId()

    if (userId != null && orgId != null) {
      return `${TASK_PAGE_SIZE_STORAGE_KEY}:user:${userId}:org:${orgId}`
    }
    if (userId != null) {
      return `${TASK_PAGE_SIZE_STORAGE_KEY}:user:${userId}`
    }
    return TASK_PAGE_SIZE_STORAGE_KEY
  }
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab') as TaskTab | null
  const initialTab = tabParam && Object.values(TaskTab).includes(tabParam) ? tabParam : TaskTab.Queued
  const [activeTab, setActiveTab] = useState<TaskTab>(initialTab)
  const [tasks, setTasks] = useState<DocDotTask[]>([])
  const [allTaskIds, setAllTaskIds] = useState<number[]>([])
  const [tabCounts, setTabCounts] = useState<Record<TaskTab, number>>({
    [TaskTab.Queued]: 0,
    [TaskTab.Started]: 0,
    [TaskTab.Completed]: 0,
    [TaskTab.Failed]: 0,
  })
  const [loading, setLoading] = useState(false)
  const [selectedTasks, setSelectedTasks] = useState<Set<number>>(new Set())
  const [filterText, setFilterText] = useState('')
  const [debouncedFilterText, setDebouncedFilterText] = useState('')
  const [filterTarget, setFilterTarget] = useState<'name' | 'result'>('name')
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [expandedTask, setExpandedTask] = useState<number | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(() => {
    const scopedStorageKey = getTaskPageSizeStorageKey()
    const stored = window.localStorage.getItem(scopedStorageKey) ?? window.localStorage.getItem(TASK_PAGE_SIZE_STORAGE_KEY)
    const parsed = stored ? Number(stored) : NaN
    if ([5, 10, 20, 50, 100].includes(parsed)) {
      return parsed
    }
    return DEFAULT_PAGE_SIZE
  })
  const appTitle = useSetting(SETTINGS_KEYS.APP_TITLE, environment.appTitle)

  useEffect(() => {
    document.title = `${t('document_processing')} - ${appTitle}`
  }, [t, appTitle])

  const translateTaskResult = (result: string | null | undefined): string => {
    if (!result) return ''

    // Translate "Success. New document id X created"
    const successMatch = result.match(/^Success\.\s*New document id (\d+) created$/i)
    if (successMatch) {
      return t('task_success_new_document_created', { id: successMatch[1] })
    }

    // Translate other common patterns if needed
    if (result.toLowerCase().startsWith('success')) {
      return result.replace(/^Success/i, t('success'))
    }

    return result
  }

  const toggleTaskResult = (taskId: number) => {
    setExpandedTask((prev) => (prev === taskId ? null : taskId))
  }

  const renderResultCell = (task: DocDotTask) => {
    const resultText = translateTaskResult(task.result)
    if (!resultText) return null

    return (
      <span className="text-muted small">
        {resultText.length > 50 ? `${resultText.substring(0, 50)}...` : resultText}
      </span>
    )
  }

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedFilterText(filterText)
    }, 300)
    return () => window.clearTimeout(timeout)
  }, [filterText])

  const getStatusForTab = (tab: TaskTab): DocDotTask['status'] => {
    switch (tab) {
      case TaskTab.Queued:
        return 'PENDING'
      case TaskTab.Started:
        return 'STARTED'
      case TaskTab.Completed:
        return 'SUCCESS'
      case TaskTab.Failed:
        return 'FAILURE'
      default:
        return 'PENDING'
    }
  }

  const buildFilterParams = () => {
    const trimmedFilter = debouncedFilterText.trim()
    if (!trimmedFilter) return {}
    return filterTarget === 'result' ? { result__icontains: trimmedFilter } : { task_file_name__icontains: trimmedFilter }
  }

  const loadTasks = useCallback(async () => {
    setLoading(true)
    try {
      const taskData = await taskService.getTasksPage({
        task_name: 'consume_file',
        acknowledged: false,
        status: getStatusForTab(activeTab),
        page,
        page_size: pageSize,
        ...buildFilterParams(),
      })
      setTasks(taskData.results || [])
      setAllTaskIds((taskData.all as number[]) || [])
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }, [activeTab, debouncedFilterText, filterTarget, page, pageSize])

  const loadTabCounts = useCallback(async () => {
    try {
      const filterParams = buildFilterParams()
      const countsResponses = await Promise.all([
        taskService.getTasksPage({
          task_name: 'consume_file',
          acknowledged: false,
          status: getStatusForTab(TaskTab.Queued),
          page: 1,
          page_size: 1,
          ...filterParams,
        }),
        taskService.getTasksPage({
          task_name: 'consume_file',
          acknowledged: false,
          status: getStatusForTab(TaskTab.Started),
          page: 1,
          page_size: 1,
          ...filterParams,
        }),
        taskService.getTasksPage({
          task_name: 'consume_file',
          acknowledged: false,
          status: getStatusForTab(TaskTab.Completed),
          page: 1,
          page_size: 1,
          ...filterParams,
        }),
        taskService.getTasksPage({
          task_name: 'consume_file',
          acknowledged: false,
          status: getStatusForTab(TaskTab.Failed),
          page: 1,
          page_size: 1,
          ...filterParams,
        }),
      ])

      setTabCounts({
        [TaskTab.Queued]: countsResponses[0].count || 0,
        [TaskTab.Started]: countsResponses[1].count || 0,
        [TaskTab.Completed]: countsResponses[2].count || 0,
        [TaskTab.Failed]: countsResponses[3].count || 0,
      })
    } catch (error) {}
  }, [debouncedFilterText, filterTarget])

  useEffect(() => {
    loadTasks()
  }, [loadTasks])

  useEffect(() => {
    loadTabCounts()
  }, [loadTabCounts])

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        loadTasks()
        loadTabCounts()
      }, 5000)
      return () => clearInterval(interval)
    }
    return undefined
  }, [autoRefresh, loadTasks, loadTabCounts])

  useEffect(() => {
    const tabParam = searchParams.get('tab') as TaskTab | null
    if (tabParam && Object.values(TaskTab).includes(tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam)
      setPage(1)
    }
  }, [activeTab, searchParams])

  useEffect(() => {
    // Reset filter target to 'name' for Started and Queued tabs
    if ((activeTab === TaskTab.Started || activeTab === TaskTab.Queued) && filterTarget === 'result') {
      setFilterTarget('name')
    }
  }, [activeTab, filterTarget])

  const handleTabChange = (tab: TaskTab) => {
    setActiveTab(tab)
    setSelectedTasks(new Set())
    setFilterText('')
    setExpandedTask(null)
    setPage(1)
    // Reset filter target to 'name' for Started and Queued tabs
    if (tab === TaskTab.Started || tab === TaskTab.Queued) {
      setFilterTarget('name')
    }
    setSearchParams({ tab })
  }

  const handleTaskSelect = (taskId: number) => {
    const newSelected = new Set(selectedTasks)
    if (newSelected.has(taskId)) {
      newSelected.delete(taskId)
    } else {
      newSelected.add(taskId)
    }
    setSelectedTasks(newSelected)
  }

  const totalTasks = tabCounts[activeTab] || 0
  const totalPages = Math.max(1, Math.ceil(totalTasks / pageSize))
  const rangeStart = totalTasks === 0 ? 0 : (page - 1) * pageSize + 1
  const rangeEnd = Math.min(totalTasks, page * pageSize)
  const paginatedTasks = tasks

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    window.localStorage.setItem(getTaskPageSizeStorageKey(), String(newPageSize))
    setPage(1)
    setSelectedTasks(new Set())
  }

  useEffect(() => {
    setPage(1)
    setSelectedTasks(new Set())
  }, [filterText, filterTarget, activeTab])

  const handleSelectAll = () => {
    const safeTasks = paginatedTasks || []
    if (selectedTasks.size === safeTasks.length) {
      setSelectedTasks(new Set())
    } else {
      setSelectedTasks(new Set(safeTasks.map((t) => t.id)))
    }
  }

  const handleDismissTask = async (task: DocDotTask) => {
    try {
      await taskService.dismissTasks([task.id])
      await loadTasks()
    } catch (error) {}
  }

  const handleDismissSelected = async () => {
    try {
      let tasksToSubmit: number[]
      if (selectedTasks.size > 0) {
        tasksToSubmit = Array.from(selectedTasks)
      } else {
        // Ako nema selekcije, dismiss sve rezultate trenutnog backend filtera (tab + search).
        tasksToSubmit = allTaskIds
      }
      if (tasksToSubmit.length === 0) return
      await taskService.dismissTasks(tasksToSubmit)
      setSelectedTasks(new Set())
      await loadTasks()
    } catch (error) {}
  }

  const getTabBadgeCount = (tab: TaskTab) => {
    return tabCounts[tab] || 0
  }

  return (
    <div className={`task-list ${className || ''}`}>
      <PageHeader title={t('document_processing')} info={t('file_tasks_shows_you_documents_that_have_been_cons') || t('tasks_info')} infoLink="tasks">
        {selectedTasks.size > 0 && (
          <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => setSelectedTasks(new Set())}>
            <i className="bi bi-x"></i>&nbsp;{t('clear_selection')}
          </Button>
        )}
        <IfPermissions action={PermissionAction.Change} type={PermissionType.DocDotTask}>
          <Button
            variant="outline-primary"
            size="sm"
            className="me-2"
            onClick={handleDismissSelected}
            disabled={totalTasks === 0}
          >
            <i className="bi bi-check2-all"></i>&nbsp;{selectedTasks.size > 0 ? t('dismiss_selected') : t('dismiss_all')}
          </Button>
        </IfPermissions>
        <div className="form-inline d-flex align-items-center task-filter-group">
          <span className="task-filter-label">{t('filter_by')}</span>
          <Dropdown
            onSelect={(key) => {
              if (key === 'name' || key === 'result') {
                setFilterTarget(key as 'name' | 'result')
              }
            }}
          >
            <Dropdown.Toggle variant="outline-primary" size="sm" className="task-filter-dropdown" id="filter-target-dropdown">
              {filterTarget === 'name' ? t('name') : t('result')}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item eventKey="name" active={filterTarget === 'name'}>
                {t('name')}
              </Dropdown.Item>
              {(activeTab === TaskTab.Completed || activeTab === TaskTab.Failed) && (
                <Dropdown.Item eventKey="result" active={filterTarget === 'result'}>
                  {t('result')}
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
          <div className="task-filter-input-wrapper position-relative">
            {filterText && (
              <Button
                variant="link"
                size="sm"
                className="px-2 position-absolute top-0 end-0 z-10"
                onClick={() => setFilterText('')}
                style={{ height: '100%', display: 'flex', alignItems: 'center' }}
              >
                <i className="bi bi-x" style={{ width: '1em', height: '1em' }}></i>
              </Button>
            )}
            <Form.Control
              type="text"
              size="sm"
              className="task-filter-input"
              placeholder={filterTarget === 'name' ? t('filter_filename') : t('filter_by_result')}
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </div>
        </div>
        <div className="form-check form-switch mb-0 ms-2">
          <Form.Check
            type="switch"
            id="auto-refresh"
            label={t('auto_refresh')}
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
        </div>
      </PageHeader>

      {loading && (
        <div className="d-flex justify-content-center">
          <Spinner size="sm" />
        </div>
      )}

      <Tabs activeKey={activeTab} onSelect={(k) => k && handleTabChange(k as TaskTab)} className="mb-3">
        <Tab
          eventKey={TaskTab.Failed}
          title={
            <span>
              {t('failed')}
              {getTabBadgeCount(TaskTab.Failed) > 0 && <Badge bg="danger">{getTabBadgeCount(TaskTab.Failed)}</Badge>}
            </span>
          }
        >
          <div className="task-list-card">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>
                    <Form.Check
                      type="checkbox"
                      checked={selectedTasks.size === (paginatedTasks || []).length && (paginatedTasks || []).length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>{t('name')}</th>
                  <th className="d-none d-lg-table-cell">{t('created')}</th>
                  <th className="d-none d-lg-table-cell">{t('result')}</th>
                  <th className="text-end">{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTasks.map((task) => (
                  <React.Fragment key={task.id}>
                    <tr>
                      <td>
                        <Form.Check type="checkbox" checked={selectedTasks.has(task.id)} onChange={() => handleTaskSelect(task.id)} />
                      </td>
                      <td>{task.task_file_name}</td>
                      <td className="d-none d-lg-table-cell">{formatDate(task.date_created)}</td>
                      <td
                        className={`d-none d-lg-table-cell task-result-cell${task.result ? '' : ' is-empty'}`}
                        onClick={() => task.result && toggleTaskResult(task.id)}
                      >
                        {renderResultCell(task)}
                      </td>
                      <td className="text-end">
                        <div className="btn-group" role="group">
                          <IfPermissions action={PermissionAction.Change} type={PermissionType.DocDotTask}>
                            <Button variant="outline-secondary" size="sm" onClick={() => handleDismissTask(task)}>
                              <i className="bi bi-check"></i> {t('dismiss')}
                            </Button>
                          </IfPermissions>
                          {task.related_document && (
                            <IfPermissions action={PermissionAction.View} type={PermissionType.Document}>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => window.open(`/documents/${task.related_document}/details`, '_blank')}
                              >
                                <i className="bi bi-file-text"></i> {t('open')}
                              </Button>
                            </IfPermissions>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedTask === task.id && task.result && (
                      <tr>
                        <td colSpan={5}>
                          <div className="task-table__result-details">
                            <pre className="small mb-0">{translateTaskResult(task.result)}</pre>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="text-muted small">
                {totalTasks > 0 ? `${rangeStart} - ${rangeEnd} / ${totalTasks}` : '0 / 0'}
                {selectedTasks.size > 0 && (
                  <span className="ms-2">
                    ({selectedTasks.size} {t('selected')})
                  </span>
                )}
              </div>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                alwaysVisible
                pageSize={pageSize}
                pageSizeOptions={[5, 10, 20, 50, 100]}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </div>
        </Tab>
        <Tab
          eventKey={TaskTab.Completed}
          title={
            <span>
              {t('completed')}
              {getTabBadgeCount(TaskTab.Completed) > 0 && <Badge bg="secondary">{getTabBadgeCount(TaskTab.Completed)}</Badge>}
            </span>
          }
        >
          <div className="task-list-card">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>
                    <Form.Check
                      type="checkbox"
                      checked={selectedTasks.size === (paginatedTasks || []).length && (paginatedTasks || []).length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>{t('name')}</th>
                  <th className="d-none d-lg-table-cell">{t('created')}</th>
                  <th className="d-none d-lg-table-cell">{t('result')}</th>
                  <th className="text-end">{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTasks.map((task) => (
                  <React.Fragment key={task.id}>
                    <tr>
                      <td>
                        <Form.Check type="checkbox" checked={selectedTasks.has(task.id)} onChange={() => handleTaskSelect(task.id)} />
                      </td>
                      <td>{task.task_file_name}</td>
                      <td className="d-none d-lg-table-cell">{formatDate(task.date_created)}</td>
                      <td
                        className={`d-none d-lg-table-cell task-result-cell${task.result ? '' : ' is-empty'}`}
                        onClick={() => task.result && toggleTaskResult(task.id)}
                      >
                        {renderResultCell(task)}
                      </td>
                      <td className="text-end">
                        <div className="btn-group" role="group">
                          <IfPermissions action={PermissionAction.Change} type={PermissionType.DocDotTask}>
                            <Button variant="outline-secondary" size="sm" onClick={() => handleDismissTask(task)}>
                              <i className="bi bi-check"></i> {t('dismiss')}
                            </Button>
                          </IfPermissions>
                          {task.related_document && (
                            <IfPermissions action={PermissionAction.View} type={PermissionType.Document}>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => window.open(`/documents/${task.related_document}/details`, '_blank')}
                              >
                                <i className="bi bi-file-text"></i> {t('open')}
                              </Button>
                            </IfPermissions>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedTask === task.id && task.result && (
                      <tr>
                        <td colSpan={5}>
                          <div className="task-table__result-details">
                            <pre className="small mb-0">{translateTaskResult(task.result)}</pre>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="text-muted small">
                {totalTasks > 0 ? `${rangeStart} - ${rangeEnd} / ${totalTasks}` : '0 / 0'}
                {selectedTasks.size > 0 && (
                  <span className="ms-2">
                    ({selectedTasks.size} {t('selected')})
                  </span>
                )}
              </div>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                alwaysVisible
                pageSize={pageSize}
                pageSizeOptions={[5, 10, 20, 50, 100]}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </div>
        </Tab>
        <Tab
          eventKey={TaskTab.Started}
          title={
            <span>
              {t('started')}
              {getTabBadgeCount(TaskTab.Started) > 0 && <Badge bg="secondary">{getTabBadgeCount(TaskTab.Started)}</Badge>}
            </span>
          }
        >
          <div className="task-list-card">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>
                    <Form.Check
                      type="checkbox"
                      checked={selectedTasks.size === (paginatedTasks || []).length && (paginatedTasks || []).length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>{t('name')}</th>
                  <th className="d-none d-lg-table-cell">{t('created')}</th>
                  <th className="text-end">{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTasks.map((task) => (
                  <React.Fragment key={task.id}>
                    <tr>
                      <td>
                        <Form.Check type="checkbox" checked={selectedTasks.has(task.id)} onChange={() => handleTaskSelect(task.id)} />
                      </td>
                      <td>{task.task_file_name}</td>
                      <td className="d-none d-lg-table-cell">{formatDate(task.date_created)}</td>
                      <td className="text-end">
                        <div className="btn-group" role="group">
                          <IfPermissions action={PermissionAction.Change} type={PermissionType.DocDotTask}>
                            <Button variant="outline-secondary" size="sm" onClick={() => handleDismissTask(task)}>
                              <i className="bi bi-check"></i> {t('dismiss')}
                            </Button>
                          </IfPermissions>
                          {task.related_document && (
                            <IfPermissions action={PermissionAction.View} type={PermissionType.Document}>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => window.open(`/documents/${task.related_document}/details`, '_blank')}
                              >
                                <i className="bi bi-file-text"></i> {t('open')}
                              </Button>
                            </IfPermissions>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedTask === task.id && task.result && (
                      <tr>
                        <td colSpan={4}>
                          <div className="task-table__result-details">
                            <pre className="small mb-0">{translateTaskResult(task.result)}</pre>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="text-muted small">
                {totalTasks > 0 ? `${rangeStart} - ${rangeEnd} / ${totalTasks}` : '0 / 0'}
                {selectedTasks.size > 0 && (
                  <span className="ms-2">
                    ({selectedTasks.size} {t('selected')})
                  </span>
                )}
              </div>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                alwaysVisible
                pageSize={pageSize}
                pageSizeOptions={[5, 10, 20, 50, 100]}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </div>
        </Tab>
        <Tab
          eventKey={TaskTab.Queued}
          title={
            <span>
              {t('queued')}
              {getTabBadgeCount(TaskTab.Queued) > 0 && <Badge bg="secondary">{getTabBadgeCount(TaskTab.Queued)}</Badge>}
            </span>
          }
        >
          <div className="task-list-card">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>
                    <Form.Check
                      type="checkbox"
                      checked={selectedTasks.size === (paginatedTasks || []).length && (paginatedTasks || []).length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>{t('name')}</th>
                  <th className="d-none d-lg-table-cell">{t('created')}</th>
                  <th className="text-end">{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTasks.map((task) => (
                  <React.Fragment key={task.id}>
                    <tr>
                      <td>
                        <Form.Check type="checkbox" checked={selectedTasks.has(task.id)} onChange={() => handleTaskSelect(task.id)} />
                      </td>
                      <td>{task.task_file_name}</td>
                      <td className="d-none d-lg-table-cell">{formatDate(task.date_created)}</td>
                      <td className="text-end">
                        <div className="btn-group" role="group">
                          <IfPermissions action={PermissionAction.Change} type={PermissionType.DocDotTask}>
                            <Button variant="outline-secondary" size="sm" onClick={() => handleDismissTask(task)}>
                              <i className="bi bi-check"></i> {t('dismiss')}
                            </Button>
                          </IfPermissions>
                          {task.related_document && (
                            <IfPermissions action={PermissionAction.View} type={PermissionType.Document}>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => window.open(`/documents/${task.related_document}/details`, '_blank')}
                              >
                                <i className="bi bi-file-text"></i> {t('open')}
                              </Button>
                            </IfPermissions>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedTask === task.id && task.result && (
                      <tr>
                        <td colSpan={4}>
                          <div className="task-table__result-details">
                            <pre className="small mb-0">{translateTaskResult(task.result)}</pre>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="text-muted small">
                {totalTasks > 0 ? `${rangeStart} - ${rangeEnd} / ${totalTasks}` : '0 / 0'}
                {selectedTasks.size > 0 && (
                  <span className="ms-2">
                    ({selectedTasks.size} {t('selected')})
                  </span>
                )}
              </div>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                alwaysVisible
                pageSize={pageSize}
                pageSizeOptions={[5, 10, 20, 50, 100]}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </div>
        </Tab>
      </Tabs>

      {!loading && totalTasks === 0 && (
        <div className="text-center text-muted py-5">
          <i className="bi bi-inbox display-1"></i>
          <p className="mt-3">{t('no_tasks_found')}</p>
        </div>
      )}
    </div>
  )
}
